import React, { useState } from "react";
import { api, fileUrl } from "@/planner/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CloudUpload } from "lucide-react";

// Lists this planogram's items that aren't in Dan's Sandstar library and, after he confirms, adds the
// checked ones with the price shown (POST /sandstar/cloud/add-to-library). Add-only: nothing already in
// his library is edited or removed. The price is what the machine will charge.
export default function SandstarLibraryAdd({ machine, className = "", size = "sm", onDone }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setOpen(true); setData(null); setBusy(true);
    try {
      const { data: d } = await api.get(`/sandstar/cloud/missing/${machine.id}`);
      setData(d);
      setRows(d.items.map((x) => {
        const choice = x.candidates?.length ? 0 : null;
        return { ...x, choice, pick: (!!x.cloud || choice !== null) && x.price != null, priceText: x.price != null ? x.price.toFixed(2) : "" };
      }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't check Sandstar"); setOpen(false); }
    finally { setBusy(false); }
  };
  const set = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  // The Sandstar item a row adds: its barcode match, or the cloud match Dan picked for an item with no barcode.
  const target = (r) => r.cloud || (r.choice !== null && r.candidates ? r.candidates[r.choice] : null);
  const valid = (r) => target(r) && Number(r.priceText) >= 0.01 && /^\d+(\.\d{1,2})?$/.test(r.priceText.trim());
  const picked = rows.filter((r) => r.pick && valid(r));

  const confirm = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/sandstar/cloud/add-to-library", {
        items: picked.map((r) => {
          const g = target(r);
          return { barcode: g.barcode, package_sn: g.package_sn || "", retail_price: Number(r.priceText),
                   product_id: r.product_id, image_path: g.image_path || null, in_library: !!g.in_library };
        }),
      });
      if (res.added.length) toast.success(`Added ${res.added.length} item${res.added.length === 1 ? "" : "s"} to your Sandstar library.`);
      if (res.failed.length) toast.error(`${res.failed.length} didn't go in: ${res.failed.map((f) => f.error).join("; ")}`);
      const done = new Set(res.added);
      setRows(rows.filter((r) => !(r.pick && target(r) && done.has(target(r).barcode))));
      if (!res.failed.length) setOpen(false);
      if (res.added.length && onDone) onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add to Sandstar"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size={size} className={className} onClick={load} disabled={busy} data-testid="sandstar-library-add-button"><CloudUpload className="mr-2 h-4 w-4" />Add missing items to Sandstar</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="sandstar-library-add-dialog">
          <DialogHeader><DialogTitle>Add missing items to your Sandstar library</DialogTitle></DialogHeader>
          {!data ? <p className="py-6 text-center text-sm text-muted-foreground">Checking your Sandstar library…</p> : (
            <>
              <p className="-mt-1 text-xs text-muted-foreground">
                {data.in_library} item{data.in_library === 1 ? " is" : "s are"} already in your library. The machine will charge the price you set here; you can change it later in Sandstar Ops. Nothing already in your library is changed.
              </p>
              {rows.length === 0 ? <p className="py-6 text-center text-sm">Everything on this planogram is in your Sandstar library.</p> : (
                <div className="max-h-[50vh] space-y-1.5 overflow-y-auto pr-1">
                  {rows.map((r, i) => (
                    <div key={r.product_id} className={`rounded-lg border p-2 ${target(r) ? "" : "opacity-60"}`} data-testid="sandstar-library-add-row">
                      <label className="flex items-center gap-3">
                        <input type="checkbox" checked={r.pick} disabled={!target(r)} onChange={(e) => set(i, { pick: e.target.checked })} />
                        {target(r)?.image_path ? <img src={fileUrl(target(r).image_path)} alt="" className="h-10 w-10 shrink-0 rounded object-contain" /> : <div className="h-10 w-10 shrink-0 rounded bg-muted" />}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{r.name}{r.variant && ` · ${r.variant}`}</div>
                          <div className="text-xs text-muted-foreground">
                            {r.candidates
                              ? (target(r) ? `Sandstar: ${target(r).name}${target(r).size ? ` ${target(r).size}` : ""}${target(r).in_library ? " (already in your library, links it here)" : ""}` : "Pick the Sandstar item below")
                              : <>{r.upc}{r.cloud ? (r.cloud.name && r.cloud.name !== r.name ? ` · Sandstar: ${r.cloud.name}` : "") : " · not in Sandstar's cloud library, pick a different item"}</>}
                          </div>
                        </div>
                        {target(r) && (
                          <div className="flex shrink-0 items-center gap-1 text-sm">$
                            <Input className="h-8 w-20" inputMode="decimal" value={r.priceText} onChange={(e) => set(i, { priceText: e.target.value })} data-testid="sandstar-library-add-price" />
                          </div>
                        )}
                      </label>
                      {r.candidates && (
                        <div className="mt-2 flex gap-2 overflow-x-auto pb-1" data-testid="sandstar-library-add-choices">
                          {r.candidates.map((g, k) => (
                            <button type="button" key={g.barcode} onClick={() => set(i, { choice: k, pick: true })}
                              className={`flex w-24 shrink-0 flex-col items-center rounded-md border p-1 text-center text-[11px] leading-tight ${r.choice === k ? "border-primary ring-2 ring-primary" : ""}`}>
                              {g.image_path ? <img src={fileUrl(g.image_path)} alt="" className="h-14 w-14 rounded object-contain" /> : <div className="h-14 w-14 rounded bg-muted" />}
                              <span className="mt-1 line-clamp-3">{g.name}{g.size ? ` ${g.size}` : ""}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {data.no_barcode.length > 0 && <p className="text-xs text-muted-foreground">No match in Sandstar's cloud library, so skipped (search Products > Sandstar cloud library): {data.no_barcode.join(", ")}</p>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={confirm} disabled={busy || picked.length === 0} data-testid="sandstar-library-add-confirm">
                  {busy ? "Adding…" : `Add ${picked.length} item${picked.length === 1 ? "" : "s"} to Sandstar`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
